import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography, Chip, Button, TablePagination, TableSortLabel } from "@mui/material";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
} from "recharts";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import MiniStat from "../../components/MiniStat";
import Loader from "../../components/Loader";
import { tokens } from "../../theme";
import { endPoints } from "../../constant/Environment";
import { getAllData } from "../../Utility/API";
import { TablePaginationActions } from "../../components/tableKit";

const mono = '"IBM Plex Mono", monospace';
const disp = '"Space Grotesk", sans-serif';

function fmtMoney(n) {
  const abs = Math.abs(n);
  if (abs >= 1e6) return `$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(abs / 1e3).toFixed(1)}k`;
  return `$${abs.toFixed(2)}`;
}

export default function GamesAnalytics() {
  const [rows, setRows] = useState([]);
  const [winners, setWinners] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: "pnl", direction: "desc" });

  useEffect(() => {
    setIsLoading(true);
    getAllData(`${endPoints.api.DASHBOARD_GAME_TRANSACTION}0`)
      .then((response) => {
        setIsLoading(false);
        if (response?.status === "success") setRows(response.data || []);
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
      });
  }, []);

  // Its own call: the game-transaction feed above is aggregated per game, so it
  // has no per-player row to rank winners from. This one is grouped per player
  // and game over a rolling 24 hours, server side.
  useEffect(() => {
    getAllData(`${endPoints.api.DASHBOARD_BIGGEST_WINNERS}10`)
      .then((response) => {
        if (response?.status === "success") {
          setWinners(
            (response.data || []).map((w) => ({
              pid: `#${w.idUser}`,
              p: w.player,
              game: w.gameName,
              won: Number(w.winAmount) || 0,
            }))
          );
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  // Aggregate raw transactions per game: plays, wagered, won, house P&L, actual RTP.
  const games = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      const key = r.gameid || r.gameName || "unknown";
      if (!map[key]) map[key] = { code: key, name: r.gameName || key, provider: r.provider, plays: 0, wagered: 0, won: 0, reportedRtp: r.rtp };
      const g = map[key];
      g.plays += Number(r.unique_users) || 0;
      g.wagered += Number(r.betamount) || 0;
      g.won += Number(r.winamount) || 0;
      g.rtp = r.rtp || 0;
    });
    const list = Object.values(map).map((g) => ({
      ...g,
      pnl: g.wagered - g.won,
      rtp: g.rtp,
     // rtp: g.wagered > 0 ? +((g.won / g.wagered) * 100).toFixed(1) : parseFloat(g.reportedRtp) || 0,
    }));
    list.sort((a, b) => b.pnl - a.pnl);
    list.forEach((g) => { g.tag = g.pnl < 0 ? "losing" : null; });
    if (list.length && list[0].pnl > 0) list[0].tag = "top";
    return list;
  }, [rows]);

  const sortedGames = useMemo(() => {
    const { key, direction } = sortConfig;
    const list = [...games].sort((a, b) => {
      const av = a[key] ?? "";
      const bv = b[key] ?? "";
      const cmp =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
      return direction === "asc" ? cmp : -cmp;
    });
    return list;
  }, [games, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
    }));
    setPage(0);
  };

  const sortableTh = (key, label, align) => (
    <th style={align === "right" ? { textAlign: "right" } : undefined}>
      <TableSortLabel
        active={sortConfig.key === key}
        direction={sortConfig.key === key ? sortConfig.direction : "desc"}
        onClick={() => handleSort(key)}
        sx={{ "&.MuiTableSortLabel-root": { color: "inherit" }, "& .MuiTableSortLabel-icon": { fontSize: 14 } }}
      >
        {label}
      </TableSortLabel>
    </th>
  );

  // Biggest loss first (most negative P&L at the top).
  const losing = games.filter((g) => g.pnl < 0).sort((a, b) => a.pnl - b.pnl);
  const houseNet = games.reduce((s, g) => s + g.pnl, 0);
  const totalWagered = games.reduce((s, g) => s + g.wagered, 0);
  const biggestWin = rows.reduce((best, r) => ((Number(r.winamount) || 0) > (Number(best?.winamount) || 0) ? r : best), null);
  const mostPlayed = [...games].sort((a, b) => b.plays - a.plays).slice(0, 6);

  return (
    <Box>
      <PageHeader title="Games" crumb="Monitor / Games" subtitle="Most played, biggest wins, P&L · spot losing games" />
      {isLoading && <Loader spinner={true} visible={isLoading} />}

      <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
        <Grid item xs={6} md={3}><MiniStat label="House Net P&L" value={`${houseNet < 0 ? "-" : ""}${fmtMoney(houseNet)}`} delta={houseNet >= 0 ? "edge holding" : "paying out"} up={houseNet >= 0} color={houseNet >= 0 ? tokens.secondary : tokens.danger} /></Grid>
        <Grid item xs={6} md={3}><MiniStat label="Games losing money" value={String(losing.length)} note={`of ${games.length} active`} color={tokens.danger} /></Grid>
        <Grid item xs={6} md={3}><MiniStat label="Total wagered" value={fmtMoney(totalWagered)} delta="SC handle" up color={tokens.accent} /></Grid>
        <Grid item xs={6} md={3}><MiniStat label="Biggest single win" value={biggestWin ? fmtMoney(Number(biggestWin.winamount)) : "—"} note={biggestWin?.gameName || "no wins yet"} color={tokens.primary} /></Grid>
      </Grid>

      <Grid container spacing={1.75}>
        <Grid item xs={12} lg={8}>
          <SectionCard title="Game performance" subtitle="By net P&L">
            <Box sx={{ overflowX: "auto" }}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "& th": { textAlign: "left", color: tokens.textDim, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, p: "10px 12px", borderBottom: `1px solid ${tokens.border}` }, "& td": { p: "12px", borderBottom: `1px solid ${tokens.border}`, fontSize: 13 } }}>
                <thead>
                  <tr>
                    {sortableTh("name", "Game")}
                    {sortableTh("plays", "Plays")}
                    {sortableTh("wagered", "Wagered")}
                    {sortableTh("rtp", "RTP")}
                    {sortableTh("pnl", "Net P&L", "right")}
                  </tr>
                </thead>
                <tbody>
                  {games.length === 0 && !isLoading && (
                    <tr><td colSpan={5} style={{ textAlign: "center", color: tokens.textDim }}>No game transactions found</td></tr>
                  )}
                  {sortedGames
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((g) => (
                    <tr key={g.code}>
                      <td>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{g.name}</Typography>
                          {g.tag === "losing" && <Chip size="small" label="losing" sx={{ height: 18, fontSize: 10, bgcolor: "rgba(242,92,102,0.13)", color: tokens.danger }} />}
                          {g.tag === "top" && <Chip size="small" label="top" sx={{ height: 18, fontSize: 10, bgcolor: "rgba(35,214,160,0.13)", color: tokens.secondary }} />}
                        </Box>
                        <Typography sx={{ fontFamily: mono, fontSize: 10.5, color: tokens.textDim2 }}>{g.code}</Typography>
                      </td>
                      <td style={{ fontFamily: mono }}>{g.plays.toLocaleString()}</td>
                      <td style={{ fontFamily: mono }}>{fmtMoney(g.wagered)}</td>
                      <td style={{ fontFamily: mono, color: g.rtp > 100 ? tokens.danger : tokens.text }}>{g.rtp}</td>
                      <td style={{ fontFamily: mono, fontWeight: 700, textAlign: "right", color: g.pnl < 0 ? tokens.danger : tokens.secondary }}>
                        {g.pnl < 0 ? "-" : ""}{fmtMoney(g.pnl)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>

            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 50]}
              count={games.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              ActionsComponent={TablePaginationActions}
              sx={{ borderTop: `1px solid ${tokens.border}`, mt: 1 }}
            />
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <SectionCard title="Biggest winners · 24h" subtitle="Payout exposure to watch">
            {winners.length === 0 && !isLoading && (
              <Typography sx={{ fontSize: 12.5, color: tokens.textDim }}>No wins recorded</Typography>
            )}
            {winners.map((w, i) => (
              <Box key={`${w.pid}-${i}`} sx={{ display: "flex", alignItems: "center", py: 1.1, borderBottom: i < winners.length - 1 ? `1px solid ${tokens.border}` : "none" }}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{w.p}</Typography>
                  <Typography sx={{ fontFamily: mono, fontSize: 10.5, color: tokens.textDim2 }}>{w.pid} · {w.game}</Typography>
                </Box>
                <Typography sx={{ ml: "auto", fontFamily: disp, fontWeight: 700, color: tokens.secondary }}>+${w.won.toLocaleString()}</Typography>
              </Box>
            ))}
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={7}>
          <SectionCard title="Most played" subtitle="By round count">
            <Box sx={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={mostPlayed} margin={{ left: -16, right: 8, top: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={tokens.border} vertical={false} />
                  <XAxis dataKey="name" stroke={tokens.textDim} fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => v.split(" ")[0]} />
                  <YAxis stroke={tokens.textDim} fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <RTooltip cursor={{ fill: "rgba(23,26,43,0.04)" }} contentStyle={{ background: tokens.surface2, border: `1px solid ${tokens.border}`, borderRadius: 8 }} />
                  <Bar dataKey="plays" radius={[6, 6, 0, 0]} fill={tokens.primary} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={5}>
          <SectionCard title="Losing games — action needed" subtitle="RTP above 100% = paying out more than taken in">
            {losing.length === 0 && !isLoading && (
              <Typography sx={{ fontSize: 12.5, color: tokens.textDim }}>No losing games — house edge holding</Typography>
            )}
            <Box sx={{ maxHeight: 260, overflowY: "auto", pr: 0.5 }}>
            {losing.map((g, i) => (
              <Box key={g.code} sx={{ display: "flex", alignItems: "center", py: 0.9, borderBottom: i < losing.length - 1 ? `1px solid ${tokens.border}` : "none" }}>
                <Typography sx={{ fontSize: 12.5 }}>{g.name}</Typography>
                <Typography sx={{ ml: "auto", fontSize: 11, color: tokens.textDim }}>RTP {g.rtp}%</Typography>
                <Typography sx={{ ml: 1.5, fontFamily: mono, color: tokens.danger, fontWeight: 600 }}>-${Math.abs(g.pnl).toLocaleString()}</Typography>
              </Box>
            ))}
            </Box>
            {losing.length > 0 && <Button size="small" variant="outlined" color="inherit" sx={{ mt: 1.5 }}>Review configs</Button>}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
